import { z } from 'zod';
import { LocalizedStringSchema } from '../content/studio';
import { ReservationBookingPolicySchema, type LeadInquiryAssessment } from './reservationInquiry';

export const FormFieldTypeSchema = z.enum([
  'text',
  'email',
  'tel',
  'textarea',
  'number',
  'date',
  'time',
]);

export const FormFieldDefinitionSchema = z.object({
  name: z.string().min(1),
  type: FormFieldTypeSchema,
  required: z.boolean().default(false),
  label: LocalizedStringSchema,
});

/** Split comma/semicolon-separated notify addresses (deduped, order preserved). */
export function parseNotifyEmails(raw: string | undefined | null): string[] {
  if (!raw?.trim()) {
    return [];
  }
  const seen = new Set<string>();
  const emails: string[] = [];
  for (const part of raw.split(/[,;]+/)) {
    const email = part.trim();
    if (!email) {
      continue;
    }
    const key = email.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    emails.push(email);
  }
  return emails;
}

export function normalizeNotifyEmailField(raw: string | undefined | null): string | undefined {
  const emails = parseNotifyEmails(raw);
  return emails.length > 0 ? emails.join(', ') : undefined;
}

/** One email, or several separated by comma/semicolon. Empty → undefined. */
export const NotifyEmailListSchema = z
  .string()
  .refine(
    (value) => {
      if (value.trim() === '') {
        return true;
      }
      return parseNotifyEmails(value).every((email) => z.string().email().safeParse(email).success);
    },
    { message: 'One or more email addresses are invalid' },
  )
  .transform((value) => normalizeNotifyEmailField(value));

export const FormRoutingSchema = z.object({
  // `.optional()` outside the transform keeps the object key omittable under Zod 4.
  notifyEmail: NotifyEmailListSchema.optional(),
  webhookUrl: z.string().url().optional(),
  /** Level 1 reservation rules (Formulare → Rules). Ignored for non-reservation forms. */
  bookingPolicy: ReservationBookingPolicySchema.optional(),
});

export const FormDefinitionPatchSchema = z.object({
  fields: z.array(FormFieldDefinitionSchema).min(1),
  routing: FormRoutingSchema.default({}),
  gdprConsentText: LocalizedStringSchema.default({}),
});

export const PublicLeadSubmitSchema = z.object({
  formKey: z.string().min(1),
  payload: z.record(z.string(), z.unknown()),
  locale: z.string().min(2),
  sourcePageSlug: z.string().optional(),
});

export type FormFieldDefinition = z.infer<typeof FormFieldDefinitionSchema>;
export type FormRouting = z.infer<typeof FormRoutingSchema>;
export type FormDefinitionPatch = z.infer<typeof FormDefinitionPatchSchema>;

export interface FormDefinitionView {
  id: string;
  formKey: string;
  fields: FormFieldDefinition[];
  routing: FormRouting;
  gdprConsentText: Record<string, string>;
}

export interface LeadSubmissionView {
  id: string;
  formKey: string;
  payload: Record<string, unknown>;
  locale: string;
  sourcePageSlug: string | null;
  status: 'new' | 'read' | 'forwarded' | 'confirmed' | 'cancelled';
  submittedAt: string;
  assessment?: LeadInquiryAssessment | null;
}

/** Posteingang default: still waiting on the owner. Confirmed bookings live on Reservierungen. */
export const INBOX_OPEN_STATUSES = ['new', 'forwarded'] as const satisfies readonly LeadSubmissionView['status'][];

/** Opt-in list: read inquiries and owner-declined reservation requests. */
export const INBOX_CLOSED_STATUSES = ['read', 'cancelled'] as const satisfies readonly LeadSubmissionView['status'][];

export type InboxListView = 'open' | 'closed';

export function inboxStatusesForView(view: InboxListView): readonly LeadSubmissionView['status'][] {
  return view === 'closed' ? INBOX_CLOSED_STATUSES : INBOX_OPEN_STATUSES;
}

export function buildDefaultReservationFormFields(
  locales: string[],
): FormFieldDefinition[] {
  const label = (de: string, en: string, vi: string) => {
    const record: Record<string, string> = {};
    for (const locale of locales) {
      record[locale] = locale === 'de' ? de : locale === 'vi' ? vi : en;
    }
    return record;
  };
  return [
    { name: 'name', type: 'text', required: true, label: label('Name', 'Name', 'Họ tên') },
    { name: 'email', type: 'email', required: true, label: label('E-Mail', 'Email', 'Email') },
    { name: 'phone', type: 'tel', required: true, label: label('Telefon', 'Phone', 'Điện thoại') },
    { name: 'guests', type: 'number', required: true, label: label('Personen', 'Guests', 'Số khách') },
    { name: 'date', type: 'date', required: true, label: label('Datum', 'Date', 'Ngày') },
    { name: 'time', type: 'time', required: true, label: label('Uhrzeit', 'Time', 'Giờ') },
    { name: 'message', type: 'textarea', required: false, label: label('Nachricht', 'Message', 'Ghi chú') },
  ];
}

export function buildDefaultAllergyFormFields(locales: string[]): FormFieldDefinition[] {
  const label = (de: string, en: string, vi: string) => {
    const record: Record<string, string> = {};
    for (const locale of locales) {
      record[locale] = locale === 'de' ? de : locale === 'vi' ? vi : en;
    }
    return record;
  };
  return [
    { name: 'name', type: 'text', required: true, label: label('Name', 'Name', 'Họ tên') },
    { name: 'email', type: 'email', required: true, label: label('E-Mail', 'Email', 'Email') },
    {
      name: 'allergens',
      type: 'textarea',
      required: true,
      label: label('Allergene / Unverträglichkeiten', 'Allergies / intolerances', 'Dị ứng'),
    },
    { name: 'consent', type: 'text', required: true, label: label('Einwilligung', 'Consent', 'Đồng ý') },
  ];
}

export function buildDefaultContactFormFields(locales: string[]): FormFieldDefinition[] {
  const label = (de: string, en: string, vi: string) => {
    const record: Record<string, string> = {};
    for (const locale of locales) {
      record[locale] = locale === 'de' ? de : locale === 'vi' ? vi : en;
    }
    return record;
  };
  return [
    { name: 'name', type: 'text', required: true, label: label('Name', 'Name', 'Họ tên') },
    { name: 'email', type: 'email', required: true, label: label('E-Mail', 'Email', 'Email') },
    { name: 'phone', type: 'tel', required: false, label: label('Telefon', 'Phone', 'Điện thoại') },
    { name: 'message', type: 'textarea', required: true, label: label('Nachricht', 'Message', 'Tin nhắn') },
  ];
}

export function buildDefaultPreOrderFormFields(locales: string[]): FormFieldDefinition[] {
  const label = (de: string, en: string, vi: string) => {
    const record: Record<string, string> = {};
    for (const locale of locales) {
      record[locale] = locale === 'de' ? de : locale === 'vi' ? vi : en;
    }
    return record;
  };
  return [
    { name: 'name', type: 'text', required: true, label: label('Name', 'Name', 'Họ tên') },
    { name: 'email', type: 'email', required: true, label: label('E-Mail', 'Email', 'Email') },
    { name: 'phone', type: 'tel', required: false, label: label('Telefon', 'Phone', 'Điện thoại') },
    {
      name: 'pickup',
      type: 'text',
      required: true,
      label: label('Abholung', 'Pickup time', 'Nhận hàng'),
    },
    {
      name: 'items',
      type: 'textarea',
      required: true,
      label: label('Bestellung', 'Order items', 'Đơn hàng'),
    },
  ];
}
